var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { Renderer } from './Renderer';
import { degToRad } from '../utils';
import { setCanvasFill, setCanvasStroke } from './styles';
import { beginShapeClip } from './clipUtils';
import { LayerCache } from '../performance/LayerCache';
import { isBatchableRect, paintStyleKey } from '../performance/styleKey';
import { traceArcSector } from './arcSector';
import { isSubtreeDirty } from '../performance/bounds';
import { toHighContrastColor } from '../utils/a11y';
import { unwrapThemeBackgroundSrc } from '../components/uiTheme';
import { parsePathSegments } from '../utils/pathGeometry';
var CanvasRenderer = /** @class */ (function (_super) {
    __extends(CanvasRenderer, _super);
    function CanvasRenderer() {
        var _this = _super.apply(this, __spreadArray([], __read(arguments), false)) || this;
        _this.patternCache = new Map();
        _this.layerCache = new LayerCache();
        _this.batchRendering = true;
        _this.dirtyRegionsEnabled = true;
        _this.layerCacheEnabled = true;
        /** Exposed for tests — number of clearRect calls in last render. */
        _this.lastClearRectCount = 0;
        /** Exposed for tests — fill calls in last render. */
        _this.lastFillCallCount = 0;
        _this.drawCallCount = 0;
        _this.bgImage = null;
        _this.bgImageKey = null;
        _this.lastRoot = null;
        return _this;
    }
    CanvasRenderer.prototype.init = function (container, options) {
        var _a;
        this.width = options.width;
        this.height = options.height;
        this.pixelRatio = options.pixelRatio;
        this.background = options.background;
        this.highContrast = (_a = options.highContrast) !== null && _a !== void 0 ? _a : false;
        this.canvas = document.createElement('canvas');
        this.canvas.style.display = 'block';
        this.canvas.style.width = "".concat(this.width, "px");
        this.canvas.style.height = "".concat(this.height, "px");
        container.appendChild(this.canvas);
        var ctx = this.canvas.getContext('2d');
        if (!ctx)
            throw new Error('LightDraw: Canvas 2D not supported');
        this.ctx = ctx;
        this.resize(this.width, this.height, this.pixelRatio);
    };
    CanvasRenderer.prototype.resize = function (width, height, pixelRatio) {
        this.width = width;
        this.height = height;
        this.pixelRatio = pixelRatio;
        this.canvas.width = width * pixelRatio;
        this.canvas.height = height * pixelRatio;
        this.canvas.style.width = "".concat(width, "px");
        this.canvas.style.height = "".concat(height, "px");
        this.ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        this.layerCache.clear();
        this.forceFullRedraw();
    };
    CanvasRenderer.prototype.getElement = function () {
        return this.canvas;
    };
    CanvasRenderer.prototype.render = function (root, cameraMatrix) {
        var e_1, _a;
        this.lastRoot = root;
        this.lastCamera = cameraMatrix;
        var ctx = this.ctx;
        this.lastClearRectCount = 0;
        this.lastFillCallCount = 0;
        this.drawCallCount = 0;
        ctx.save();
        if (this.needsFullRedraw || !this.dirtyRegionsEnabled) {
            ctx.clearRect(0, 0, this.width, this.height);
            this.lastClearRectCount = 1;
        }
        else {
            try {
                for (var _b = __values(this.dirtyRegions), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var r = _c.value;
                    ctx.clearRect(r.x, r.y, r.width, r.height);
                    this.lastClearRectCount++;
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        if (this.background && this.background !== 'transparent') {
            this.paintBackground(ctx);
        }
        if (cameraMatrix) {
            ctx.transform(cameraMatrix.a, cameraMatrix.b, cameraMatrix.c, cameraMatrix.d, cameraMatrix.e, cameraMatrix.f);
        }
        this.drawGroup(root);
        ctx.restore();
        this.clearSceneDirty(root);
        this.clearDirty();
    };
    CanvasRenderer.prototype.paintBackground = function (ctx) {
        var src = unwrapThemeBackgroundSrc(this.background);
        if (src) {
            this.ensureBackgroundImage(src);
            var img = this.bgImage;
            if (img && img.complete && img.naturalWidth > 0) {
                var iw = img.naturalWidth;
                var ih = img.naturalHeight;
                var scale = Math.max(this.width / iw, this.height / ih);
                var dw = iw * scale;
                var dh = ih * scale;
                ctx.drawImage(img, (this.width - dw) / 2, (this.height - dh) / 2, dw, dh);
            }
            return;
        }
        ctx.fillStyle = this.background;
        ctx.fillRect(0, 0, this.width, this.height);
    };
    CanvasRenderer.prototype.ensureBackgroundImage = function (src) {
        var _this = this;
        if (this.bgImageKey === src && this.bgImage)
            return;
        this.bgImageKey = src;
        var img = new Image();
        img.decoding = 'async';
        img.onload = function () {
            if (_this.bgImageKey !== src)
                return;
            _this.bgImage = img;
            _this.forceFullRedraw();
            if (_this.lastRoot)
                _this.render(_this.lastRoot, _this.lastCamera);
        };
        img.onerror = function () {
            if (_this.bgImageKey === src)
                _this.bgImage = null;
        };
        img.src = src;
        this.bgImage = img.complete && img.naturalWidth > 0 ? img : null;
    };
    CanvasRenderer.prototype.clearSceneDirty = function (node) {
        var e_2, _a;
        node.clearDirty();
        if ('children' in node) {
            try {
                for (var _b = __values(node.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var child = _c.value;
                    this.clearSceneDirty(child);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    };
    CanvasRenderer.prototype.toDataURL = function (type, quality) {
        if (type === void 0) { type = 'image/png'; }
        return this.canvas.toDataURL(type, quality);
    };
    CanvasRenderer.prototype.destroy = function () {
        this.layerCache.destroy();
        this.canvas.remove();
    };
    CanvasRenderer.prototype.drawGroup = function (group) {
        var e_3, _a;
        group.sortChildren();
        if (this.layerCacheEnabled &&
            group.cacheAsBitmap &&
            !isSubtreeDirty(group) &&
            this.layerCache.isValid(group)) {
            var entry = this.layerCache.get(group);
            this.ctx.drawImage(entry.canvas, 0, 0);
            return;
        }
        if (this.layerCacheEnabled && group.cacheAsBitmap && !isSubtreeDirty(group)) {
            this.renderGroupToCache(group);
            var entry = this.layerCache.get(group);
            if (entry) {
                this.ctx.drawImage(entry.canvas, 0, 0);
                return;
            }
        }
        if (this.batchRendering) {
            this.drawGroupBatched(group);
            return;
        }
        try {
            for (var _b = __values(group.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                var child = _c.value;
                if (!child.visible)
                    continue;
                this.drawNode(child);
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_3) throw e_3.error; }
        }
    };
    CanvasRenderer.prototype.drawGroupBatched = function (group) {
        var e_4, _a;
        var _this = this;
        group.sortChildren();
        var batch = [];
        var batchKey = '';
        var flush = function () {
            if (batch.length === 0)
                return;
            if (batch.length > 1)
                _this.drawRectBatch(batch);
            else
                _this.drawNode(batch[0]);
            batch = [];
            batchKey = '';
        };
        try {
            for (var _b = __values(group.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                var child = _c.value;
                if (!child.visible)
                    continue;
                if (isBatchableRect(child)) {
                    var key = paintStyleKey(child);
                    if (batch.length > 0 && key !== batchKey)
                        flush();
                    batchKey = key;
                    batch.push(child);
                }
                else {
                    flush();
                    this.drawNode(child);
                }
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_4) throw e_4.error; }
        }
        flush();
    };
    CanvasRenderer.prototype.drawRectBatch = function (rects) {
        var e_5, _a;
        var ctx = this.ctx;
        var sample = rects[0];
        ctx.save();
        ctx.beginPath();
        try {
            for (var rects_1 = __values(rects), rects_1_1 = rects_1.next(); !rects_1_1.done; rects_1_1 = rects_1.next()) {
                var node = rects_1_1.value;
                ctx.rect(node.x, node.y, node.width, node.height);
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (rects_1_1 && !rects_1_1.done && (_a = rects_1.return)) _a.call(rects_1);
            }
            finally { if (e_5) throw e_5.error; }
        }
        if (sample.fill) {
            this.setFill(ctx, sample.fill);
            ctx.fill();
            this.lastFillCallCount++;
        }
        ctx.restore();
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.renderGroupToCache = function (group) {
        var e_6, _a;
        var b = group.getBounds();
        var w = Math.max(Math.ceil(b.width), 1);
        var h = Math.max(Math.ceil(b.height), 1);
        var entry = this.layerCache.get(group);
        if (!entry || entry.width < w || entry.height < h) {
            var canvas = document.createElement('canvas');
            canvas.width = w * this.pixelRatio;
            canvas.height = h * this.pixelRatio;
            var ctx_1 = canvas.getContext('2d');
            if (!ctx_1)
                return;
            ctx_1.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
            entry = { canvas: canvas, ctx: ctx_1, width: w, height: h };
            this.layerCache.set(group, entry);
        }
        var ctx = entry.ctx;
        ctx.clearRect(0, 0, entry.width, entry.height);
        var prevCtx = this.ctx;
        this.ctx = ctx;
        try {
            if (this.batchRendering)
                this.drawGroupBatched(group);
            else {
                try {
                    for (var _b = __values(group.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                        var child = _c.value;
                        if (!child.visible)
                            continue;
                        this.drawNode(child);
                    }
                }
                catch (e_6_1) { e_6 = { error: e_6_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                    }
                    finally { if (e_6) throw e_6.error; }
                }
            }
        }
        finally {
            this.ctx = prevCtx;
        }
    };
    CanvasRenderer.prototype.drawNode = function (node) {
        var ctx = this.ctx;
        ctx.save();
        ctx.globalAlpha = node.opacity;
        ctx.translate(node.x, node.y);
        ctx.rotate(degToRad(node.rotation));
        ctx.scale(node.scaleX, node.scaleY);
        if (node.skewX || node.skewY) {
            ctx.transform(1, Math.tan(degToRad(node.skewY)), Math.tan(degToRad(node.skewX)), 1, 0, 0);
        }
        if (node.shadow) {
            ctx.shadowColor = node.shadow.color;
            ctx.shadowBlur = node.shadow.blur;
            ctx.shadowOffsetX = node.shadow.offsetX;
            ctx.shadowOffsetY = node.shadow.offsetY;
        }
        if (node.clip) {
            beginShapeClip(ctx, node);
        }
        if (node.mask) {
            beginShapeClip(ctx, node.mask);
        }
        node.draw(this);
        if ('children' in node) {
            this.drawGroup(node);
        }
        if (this.focusedNodeId === node.id) {
            this.drawFocusRing(node);
        }
        ctx.restore();
    };
    CanvasRenderer.prototype.drawFocusRing = function (node) {
        var ctx = this.ctx;
        var b = node.getBounds();
        ctx.save();
        ctx.strokeStyle = this.highContrast ? '#ffff00' : '#2563eb';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 3]);
        if (typeof ctx.strokeRect === 'function') {
            ctx.strokeRect(b.x - 2, b.y - 2, b.width + 4, b.height + 4);
        }
        else {
            ctx.beginPath();
            ctx.rect(b.x - 2, b.y - 2, b.width + 4, b.height + 4);
            ctx.stroke();
        }
        ctx.restore();
    };
    CanvasRenderer.prototype.resolveFill = function (fill) {
        if (!this.highContrast || !fill || typeof fill !== 'string')
            return fill;
        return toHighContrastColor(fill, 'fill');
    };
    CanvasRenderer.prototype.resolveStroke = function (stroke) {
        if (!this.highContrast || !stroke || typeof stroke !== 'string')
            return stroke;
        return toHighContrastColor(stroke, 'stroke');
    };
    CanvasRenderer.prototype.drawRect = function (node) {
        var ctx = this.ctx;
        var width = node.width, height = node.height, cornerRadius = node.cornerRadius;
        ctx.beginPath();
        if (cornerRadius > 0) {
            this.roundRect(ctx, 0, 0, width, height, cornerRadius);
        }
        else {
            ctx.rect(0, 0, width, height);
        }
        this.fillAndStroke(ctx, node.fill, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawCircle = function (node) {
        var ctx = this.ctx;
        ctx.beginPath();
        ctx.arc(node.radius, node.radius, node.radius, 0, Math.PI * 2);
        this.fillAndStroke(ctx, node.fill, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawEllipse = function (node) {
        var ctx = this.ctx;
        ctx.beginPath();
        ctx.ellipse(node.radiusX, node.radiusY, node.radiusX, node.radiusY, 0, 0, Math.PI * 2);
        this.fillAndStroke(ctx, node.fill, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawLine = function (node) {
        var ctx = this.ctx;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(node.x2, node.y2);
        this.applyStroke(ctx, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        ctx.stroke();
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawArc = function (node) {
        var ctx = this.ctx;
        traceArcSector(ctx, node.radius, node.radius, node.radius, node.startAngle, node.endAngle, node.innerRadius, node.counterClockwise);
        this.fillAndStroke(ctx, node.fill, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawPolygon = function (node) {
        var ctx = this.ctx;
        var pts = node.points;
        if (pts.length < 4)
            return;
        ctx.beginPath();
        ctx.moveTo(pts[0], pts[1]);
        for (var i = 2; i < pts.length; i += 2) {
            ctx.lineTo(pts[i], pts[i + 1]);
        }
        ctx.closePath();
        this.fillAndStroke(ctx, node.fill, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawPolyline = function (node) {
        var ctx = this.ctx;
        var pts = node.points;
        if (pts.length < 4)
            return;
        ctx.beginPath();
        ctx.moveTo(pts[0], pts[1]);
        for (var i = 2; i < pts.length; i += 2) {
            ctx.lineTo(pts[i], pts[i + 1]);
        }
        this.applyStroke(ctx, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        ctx.stroke();
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawPath = function (node) {
        var e_7, _a;
        var ctx = this.ctx;
        // Path2D is missing in some test / non-browser canvases — fall back to stroke sampling.
        if (typeof Path2D === 'undefined') {
            var segs = parsePathSegments(node.d);
            try {
                for (var segs_1 = __values(segs), segs_1_1 = segs_1.next(); !segs_1_1.done; segs_1_1 = segs_1.next()) {
                    var seg = segs_1_1.value;
                    if (seg.length < 2)
                        continue;
                    ctx.beginPath();
                    ctx.moveTo(seg[0].x, seg[0].y);
                    for (var i = 1; i < seg.length; i++)
                        ctx.lineTo(seg[i].x, seg[i].y);
                    if (node.fill) {
                        this.setFill(ctx, node.fill);
                        ctx.fill();
                        this.lastFillCallCount++;
                    }
                    if (node.stroke) {
                        this.applyStroke(ctx, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
                        ctx.stroke();
                    }
                }
            }
            catch (e_7_1) { e_7 = { error: e_7_1 }; }
            finally {
                try {
                    if (segs_1_1 && !segs_1_1.done && (_a = segs_1.return)) _a.call(segs_1);
                }
                finally { if (e_7) throw e_7.error; }
            }
            this.drawCallCount++;
            return;
        }
        var path = new Path2D(node.d);
        if (node.fill) {
            this.setFill(ctx, node.fill);
            ctx.fill(path);
            this.lastFillCallCount++;
        }
        if (node.stroke) {
            this.applyStroke(ctx, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
            ctx.stroke(path);
        }
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawStar = function (node) {
        var ctx = this.ctx;
        var numPoints = node.numPoints, innerRadius = node.innerRadius, outerRadius = node.outerRadius;
        var cx = outerRadius;
        var cy = outerRadius;
        ctx.beginPath();
        for (var i = 0; i < numPoints * 2; i++) {
            var r = i % 2 === 0 ? outerRadius : innerRadius;
            var angle = (i * Math.PI) / numPoints - Math.PI / 2;
            var x = cx + r * Math.cos(angle);
            var y = cy + r * Math.sin(angle);
            if (i === 0)
                ctx.moveTo(x, y);
            else
                ctx.lineTo(x, y);
        }
        ctx.closePath();
        this.fillAndStroke(ctx, node.fill, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawText = function (node) {
        var ctx = this.ctx;
        ctx.font = "".concat(node.fontWeight, " ").concat(node.fontSize, "px ").concat(node.fontFamily);
        ctx.textAlign = node.textAlign;
        ctx.textBaseline = 'top';
        if (node.fill) {
            this.setFill(ctx, node.fill);
            ctx.fillText(node.text, 0, 0);
            this.lastFillCallCount++;
        }
        if (node.stroke) {
            this.applyStroke(ctx, node.stroke, node.strokeWidth, node.dash, node.dashOffset, node.lineCap, node.lineJoin);
            ctx.strokeText(node.text, 0, 0);
        }
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawImage = function (node) {
        if (!node.image)
            return;
        var ctx = this.ctx;
        ctx.drawImage(node.image, 0, 0, node.width, node.height);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.drawSprite = function (node) {
        if (!node.image)
            return;
        var ctx = this.ctx;
        var col = node.currentFrame;
        ctx.drawImage(node.image, col * node.frameWidth, 0, node.frameWidth, node.frameHeight, 0, 0, node.width, node.height);
        this.drawCallCount++;
    };
    CanvasRenderer.prototype.getLastDrawCallCount = function () {
        return this.drawCallCount;
    };
    CanvasRenderer.prototype.roundRect = function (ctx, x, y, w, h, r) {
        r = Math.min(r, w / 2, h / 2);
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
    };
    CanvasRenderer.prototype.fillAndStroke = function (ctx, fill, stroke, strokeWidth, dash, dashOffset, lineCap, lineJoin) {
        var resolvedFill = this.resolveFill(fill);
        var resolvedStroke = this.resolveStroke(stroke);
        if (resolvedFill) {
            this.setFill(ctx, resolvedFill);
            ctx.fill();
            this.lastFillCallCount++;
        }
        if (resolvedStroke) {
            this.applyStroke(ctx, resolvedStroke, strokeWidth, dash, dashOffset, lineCap, lineJoin);
            ctx.stroke();
        }
    };
    CanvasRenderer.prototype.setFill = function (ctx, fill) {
        setCanvasFill(ctx, fill, this.patternCache);
    };
    CanvasRenderer.prototype.applyStroke = function (ctx, stroke, strokeWidth, dash, dashOffset, lineCap, lineJoin) {
        if (!stroke)
            return;
        setCanvasStroke(ctx, stroke);
        ctx.lineWidth = strokeWidth;
        ctx.lineCap = lineCap;
        ctx.lineJoin = lineJoin;
        if (dash.length > 0)
            ctx.setLineDash(dash);
        ctx.lineDashOffset = dashOffset;
    };
    return CanvasRenderer;
}(Renderer));
export { CanvasRenderer };
